"use client";

import { useKitseed } from "../lib/provider";
import { AlertDialog } from "@base-ui/react/alert-dialog";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./button";
import { Input } from "./field";

export const modalBackdropClassName = [
  "fixed inset-0 z-50 bg-[var(--scrim-solid)] supports-[backdrop-filter:blur(0)]:bg-[var(--scrim)]",
  "backdrop-blur-[10px] backdrop-saturate-[1.2]",
  "transition-opacity duration-[220ms] ease-out data-ending-style:duration-150",
  "data-starting-style:opacity-0 data-ending-style:opacity-0 motion-reduce:transition-none",
].join(" ");

export const modalPopupMotionClassName = [
  "transition-[opacity,scale] duration-[240ms] ease-[cubic-bezier(0.22,1,0.36,1)] data-ending-style:duration-150",
  "data-starting-style:scale-[0.97] data-starting-style:opacity-0",
  "data-ending-style:scale-[0.98] data-ending-style:opacity-0 motion-reduce:transition-none",
].join(" ");

const popupClassName = [
  "fixed left-1/2 top-1/2 z-50 max-h-[calc(100dvh-1rem)] w-[calc(100vw-2rem)] max-w-100 -translate-x-1/2 -translate-y-1/2 overflow-y-auto overscroll-contain",
  "rounded-window border border-line bg-elevated p-5 shadow-popup outline-none",
  modalPopupMotionClassName,
].join(" ");

export type ConfirmDialogViewProps = {
  trigger?: ReactElement<Record<string, unknown>> | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenChangeComplete?: ((open: boolean) => void) | undefined;
  finalFocus?: ComponentProps<typeof AlertDialog.Popup>["finalFocus"];
  title: string;
  description: ReactNode;
  busy?: boolean;
  children: ReactNode;
};

/** Accessible modal surface; hosts may supply their own confirmation policy and controls. */
export function ConfirmDialogView({
  trigger,
  open,
  onOpenChange,
  onOpenChangeComplete,
  finalFocus,
  title,
  description,
  busy,
  children,
}: ConfirmDialogViewProps) {
  return (
    <AlertDialog.Root
      open={open}
      onOpenChange={onOpenChange}
      {...(onOpenChangeComplete ? { onOpenChangeComplete } : {})}
    >
      {trigger ? <AlertDialog.Trigger render={trigger} /> : null}
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className={modalBackdropClassName} />
        <AlertDialog.Popup
          className={popupClassName}
          aria-busy={busy || undefined}
          {...(finalFocus !== undefined ? { finalFocus } : {})}
        >
          <AlertDialog.Title className="text-[15px] font-semibold text-fg">
            {title}
          </AlertDialog.Title>
          <AlertDialog.Description className="mt-2 text-[13px] leading-6 text-fg-muted">
            {description}
          </AlertDialog.Description>
          {children}
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

export const ConfirmDialogClose = AlertDialog.Close;

/**
 * Replaces `window.confirm` for destructive actions: it keeps focus management
 * inside the app, is styleable, and does not block the main thread.
 */
export function ConfirmDialog({
  trigger,
  open: controlledOpen,
  onOpenChange,
  onOpenChangeComplete,
  finalFocus,
  title,
  description,
  confirmLabel,
  confirmationText,
  confirmDisabled = false,
  cancelLabel,
  destructive = true,
  onPrepare,
  onConfirm,
  renderError,
}: {
  trigger?: ReactElement<Record<string, unknown>>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onOpenChangeComplete?: (open: boolean) => void;
  finalFocus?: ComponentProps<typeof AlertDialog.Popup>["finalFocus"];
  title: string;
  description: ReactNode;
  confirmLabel: string;
  confirmationText?: string;
  confirmDisabled?: boolean;
  cancelLabel?: string;
  destructive?: boolean;
  onPrepare?: () => void | Promise<void>;
  onConfirm: () => void | boolean | Promise<void | boolean>;
  renderError?: (error: unknown) => ReactNode;
}) {
  const { messages } = useKitseed();
  const [preparationFailed, setPreparationFailed] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [typedConfirmation, setTypedConfirmation] = useState("");
  const open = controlledOpen ?? uncontrolledOpen;

  const generation = useRef(0);
  const busyLock = useRef(false);
  const prepareCallback = useRef(onPrepare);
  prepareCallback.current = onPrepare;

  function setOpen(nextOpen: boolean) {
    if (controlledOpen === undefined) setUncontrolledOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }

  const prepare = useCallback(async () => {
    const visit = ++generation.current;
    setFailure(null);
    setPreparationFailed(false);
    const callback = prepareCallback.current;
    setPreparing(Boolean(callback));
    if (!callback) return;
    try {
      await callback();
    } catch (error) {
      if (visit !== generation.current) return;
      setFailure(error);
      setPreparationFailed(true);
    } finally {
      if (visit === generation.current) setPreparing(false);
    }
  }, []);

  useEffect(() => {
    if (open) void prepare();
    else {
      generation.current += 1;
      setPreparing(false);
      setTypedConfirmation("");
      setFailure(null);
      setPreparationFailed(false);
      setBusy(false);
      busyLock.current = false;
    }
    return () => {
      generation.current += 1;
    };
  }, [open, prepare]);

  async function confirm() {
    if (
      busyLock.current ||
      preparing ||
      preparationFailed ||
      confirmDisabled ||
      (confirmationText && typedConfirmation !== confirmationText)
    )
      return;
    const visit = generation.current;
    busyLock.current = true;
    setFailure(null);
    setBusy(true);
    try {
      const shouldClose = await onConfirm();
      if (visit === generation.current && shouldClose !== false) setOpen(false);
    } catch (error) {
      if (visit === generation.current) setFailure(error);
    } finally {
      if (visit === generation.current) {
        setBusy(false);
        busyLock.current = false;
      }
    }
  }

  function changeOpen(nextOpen: boolean) {
    if (busyLock.current) return;
    if (nextOpen && prepareCallback.current) setPreparing(true);
    setOpen(nextOpen);
  }

  return (
    <ConfirmDialogView
      trigger={trigger}
      open={open}
      onOpenChange={changeOpen}
      onOpenChangeComplete={onOpenChangeComplete}
      finalFocus={finalFocus}
      title={title}
      description={description}
      busy={busy || preparing}
    >
      {confirmationText ? (
        <label className="mt-4 grid gap-1.5 text-[13px] text-fg-muted">
          {messages.confirmText(confirmationText)}
          <Input
            className="h-11 sm:h-8"
            value={typedConfirmation}
            onChange={(event) => setTypedConfirmation(event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
      ) : null}
      <div className="mt-5 grid gap-3 pb-[env(safe-area-inset-bottom)]">
        {failure ? (
          <div
            role="alert"
            className="rounded-control border border-danger-line bg-danger-subtle p-3 text-[13px] text-danger-text"
          >
            {renderError ? renderError(failure) : messages.actionFailed}
            {preparationFailed ? (
              <Button variant="secondary" onClick={() => void prepare()}>
                {messages.retry}
              </Button>
            ) : null}
          </div>
        ) : null}
        <div className="flex justify-end gap-2">
          <AlertDialog.Close
            render={
              <Button variant="secondary" className="min-h-11 sm:min-h-0" disabled={busy}>
                {cancelLabel ?? messages.cancel}
              </Button>
            }
          />
          <Button
            variant={destructive ? "danger" : "primary"}
            className="min-h-11 sm:min-h-0"
            disabled={
              busy ||
              preparing ||
              preparationFailed ||
              confirmDisabled ||
              Boolean(confirmationText && typedConfirmation !== confirmationText)
            }
            onClick={() => void confirm()}
          >
            {preparing ? messages.preparing : busy ? messages.submitting : confirmLabel}
          </Button>
        </div>
      </div>
    </ConfirmDialogView>
  );
}
